import React from 'react';
import { NameEmailAvatar } from '../../../../../../ui';

// Apple Glass Tooltip component
const TooltipWrapper: React.FC<{ text: string; children?: React.ReactNode }> = ({
  text,
  children,
}) => {
  if (!text || text === '-') {
    return <span className='text-slate-300 font-light select-none'>—</span>;
  }

  return (
    <div className='relative group/cell w-full flex items-center justify-start text-left'>
      { children ? (
        children
      ) : (
        <div className='max-w-[200px] truncate whitespace-nowrap overflow-hidden text-[13px] font-medium text-slate-800'>
          { text }
        </div>
      ) }
      { /* Apple Glass Tooltip */ }
      { text && text.length > 15 && (
        <div className='absolute z-[9999] opacity-0 group-hover/cell:opacity-100 transition-opacity duration-200 pointer-events-none left-0 bottom-full mb-1.5'>
          <div className='bg-slate-900/95 text-slate-50 text-[11px] font-medium rounded-lg px-2.5 py-1.5 shadow-xl whitespace-nowrap backdrop-blur-md border border-white/10'>
            { text }
          </div>
        </div>
      ) }
    </div>
  );
};

// Robust date formatter handling ISO strings, timestamps, and formatted dates
const formatDate = (rawDate: number | string | undefined | null) => {
  if (!rawDate || rawDate === '-' || rawDate === 'null' || rawDate === 'undefined') {
    return '—';
  }

  if (typeof rawDate === 'string' && /^\d{2}[/-]\d{2}[/-]\d{4}$/.test(rawDate.trim())) {
    return rawDate.trim();
  }

  let date: Date;
  if (typeof rawDate === 'number' || /^\d+$/.test(String(rawDate).trim())) {
    const num = Number(rawDate);
    date = num < 10000000000 ? new Date(num * 1000) : new Date(num);
  } else {
    date = new Date(rawDate);
  }

  if (isNaN(date.getTime())) {
    return String(rawDate);
  }

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const columns = [
  { title: 'Name' },
  { title: 'User Type' },
  { title: 'Roles Assigned' },
  { title: 'Assessments' },
  { title: 'Job Applicants' },
  { title: 'Last Login' },
];

export const getColumns = columns.map((col) => {
  if (col.title === 'Name') {
    return {
      key: 'Name',
      label: 'User',
      width: '260px',
      align: 'left' as const,
      render: (row: any) => (
        <NameEmailAvatar
            email={row['Email']}
            name={row['Name']}
            avatar={row['Profile Picture']}
        />
      ),
      disableSort: true,
    };
  }

  if (col.title === 'User Type') {
    return {
      key: col.title,
      label: col.title,
      width: '150px',
      align: 'left' as const,
      render: (row: any) => {
        const val = row[col.title];
        if (!val || val === '-') return <span className='text-slate-300 font-light select-none'>—</span>;
        return (
          <span className='inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 font-medium text-[11px] border border-emerald-200/60'>
            { val }
          </span>
        );
      },
      disableSort: true,
    };
  }

  if (col.title === 'Assessments' || col.title === 'Job Applicants') {
    return {
      key: col.title,
      label: col.title,
      width: '140px',
      align: 'left' as const,
      render: (row: any) => {
        const val = row[col.title];
        if (val === undefined || val === null || val === '-' || val === '') {
          return <span className='text-slate-300 font-light select-none'>—</span>;
        }
        return (
          <span className='font-semibold text-[13px] text-slate-800 tabular-nums'>
            { val }
          </span>
        );
      },
      disableSort: true,
    };
  }

  return {
    key: col.title,
    label: col.title,
    width: '180px',
    align: 'left' as const,
    render: (row: any) => {
      const value = row[col.title];
      const isDateColumn = col.title === 'Last Login';
      const displayText = isDateColumn ? formatDate(value) : value;
      return <TooltipWrapper text={displayText} />;
    },
    disableSort: true,
  };
});
